/**
 * A colour per person on the board.
 *
 * Everyone who is not the therapist used to share one colour, so three people
 * in a family session had three identical pointers — which answers "someone is
 * moving that" but not "who". The palette is the board's own, the one the
 * design standards already validate in both themes.
 *
 * Assigned from the id rather than from arrival order, so a pointer keeps its
 * colour when someone else leaves the room; collisions are walked to the next
 * free slot so two people present at the same time never match.
 */

export const PRESENCE_INKS = [
    "var(--chart-1)",
    "var(--chart-2)",
    "var(--chart-3)",
    "var(--chart-4)",
    "var(--chart-5)",
    "var(--chart-6)",
] as const;

/** A small stable hash: the same id always starts at the same slot. */
const slotOf = (id: string): number => {
    let hash = 0;
    for (let index = 0; index < id.length; index += 1) {
        hash = (hash * 31 + id.charCodeAt(index)) | 0;
    }
    return Math.abs(hash) % PRESENCE_INKS.length;
};

/**
 * Who gets which colour, for everyone on the board at once.
 *
 * Sorted before assigning so the answer does not depend on the order the
 * server happened to list people in.
 */
export const assignInks = (ids: string[]): Record<string, string> => {
    const taken = new Set<number>();
    const inks: Record<string, string> = {};

    for (const id of [...ids].sort()) {
        let slot = slotOf(id);
        // Past the palette there is nothing left to be distinct with, so the
        // colour goes back to being whatever the id says.
        if (taken.size < PRESENCE_INKS.length) {
            while (taken.has(slot)) slot = (slot + 1) % PRESENCE_INKS.length;
            taken.add(slot);
        }
        inks[id] = PRESENCE_INKS[slot];
    }

    return inks;
};
